import { Check, ChevronDown, ChevronUp, Map, Pencil, Plus, Trash2, X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetLandUsage } from "../../../../api/static-data";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import { Label } from "../../../../components/ui/common/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { ToggleGroup, ToggleGroupItem } from "../../../../components/ui/common/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { CULTIVATION_TYPE } from "../../../../config/constants";
import { getLastTwentyYears, validatePercentageNumber } from "../../../../utils/utils";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";
import CultivarSaveReminder, { DELETE_CONFIRM_MESSAGES } from "./CultivarSaveReminder";

interface LandUseFormValues {
  landUsageAllocation: string;
  landUseYearOfChange: string;
  landUsageFrom: string;
  landUsageTo: string;
}

const EMPTY_LAND_USE_FORM_VALUES: LandUseFormValues = {
  landUsageAllocation: "",
  landUseYearOfChange: "",
  landUsageFrom: "",
  landUsageTo: ""
};

const LandUse = forwardRef(
  (
    {
      currentFormData,
      currentDataType,
      lockStatus = false,
      disabled = false,
      sectionsDisabled = false
    }: any,
    ref
  ) => {
    const [isEditingEntry, setIsEditingEntry] = useState(false);
    const [landUsageChange, setLandUsageChange] = useState(
      currentFormData?.landUsageChange || "no"
    );
    const [currentLandUsageDetails, setCurrentLandUsageDetails] = useState<any>(
      currentFormData?.cultivationField?.landUsage || []
    );
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [formEntryKey, setFormEntryKey] = useState(0);
    const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
    const [deleteValue, setDeleteValue] = useState<any>({
      item: "",
      type: ""
    });

    useEffect(() => {
      setCurrentLandUsageDetails(currentFormData?.cultivationField?.landUsage || []);
      setLandUsageChange(currentFormData?.landUsageChange || "no");
    }, [currentFormData]);

    const { data: landUsageData, isSuccess: isLandUsageSuccess } = useGetLandUsage();

    const landUsageOptions = useMemo(() => {
      if (isLandUsageSuccess) {
        return landUsageData;
      }
      return [];
    }, [landUsageData, isLandUsageSuccess]);

    const yearOptions = useMemo(() => getLastTwentyYears(), []);

    const form = useForm<LandUseFormValues>({
      defaultValues: EMPTY_LAND_USE_FORM_VALUES,
      mode: "all",
      reValidateMode: "onChange"
    });

    const {
      control,
      handleSubmit,
      reset,
      clearErrors,
      formState: { errors }
    } = form;

    const hasErrors = Object.keys(errors).length > 0;

    const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
      sectionsDisabled,
      false,
      hasErrors
    );

    useImperativeHandle(ref, () => ({
      getLandUseData: () => currentLandUsageDetails,
      getLanduseStatus: () => landUsageChange
    }));

    const resetLandUsageForm = useCallback(() => {
      clearErrors();
      reset(EMPTY_LAND_USE_FORM_VALUES);
      setIsEditingEntry(false);
      setEditIndex(null);
      setFormEntryKey(key => key + 1);
    }, [clearErrors, reset]);

    const addLandUsage = handleSubmit(formValues => {
      const newEntry = {
        landUsageFrom: formValues.landUsageFrom,
        landUsageTo: formValues.landUsageTo,
        landUseYearOfChange: formValues.landUseYearOfChange,
        landUsageAllocation: formValues.landUsageAllocation
      };

      const updatedData = [...(currentLandUsageDetails ?? [])];

      if (isEditingEntry && editIndex !== null) {
        updatedData[editIndex] = { ...updatedData[editIndex], ...newEntry };
      } else {
        updatedData.push(newEntry);
      }

      setCurrentLandUsageDetails(updatedData);
      resetLandUsageForm();
    });

    const onEditHandler = (item: any, index: number) => {
      setIsEditingEntry(true);
      setEditIndex(index);
      setLandUsageChange("yes");
      reset({
        landUsageAllocation: item.landUsageAllocation?.toString() ?? "",
        landUseYearOfChange: item.landUseYearOfChange?.toString() ?? "",
        landUsageFrom: item.landUsageFrom?.toString() ?? "",
        landUsageTo: item.landUsageTo?.toString() ?? ""
      });
      setFormEntryKey(key => key + 1);
    };

    const deleteLandUse = useCallback(() => {
      setCurrentLandUsageDetails((prev: any[]) =>
        prev.filter((_: any, index: number) => index !== deleteValue.index)
      );
      setVisibleDeleteModal(false);
      setDeleteValue({});
    }, [deleteValue.index]);

    const deleteLandUseData = (item: any, index: number) => {
      setVisibleDeleteModal(true);
      setDeleteValue({
        type: "LandUsage",
        item: item ?? "",
        index
      });
    };

    const handleClick = toggleAccordion;

    const isLandUseEnabled = landUsageChange === "yes";
    const isAddDisabled = lockStatus || disabled;

    const getLandUsageLabel = (value: string | number) => {
      const match = landUsageOptions.find((option: any) => String(option.value) === String(value));
      return match?.text ?? match?.label ?? value;
    };

    return currentDataType === CULTIVATION_TYPE.Supplier ? (
      <Card className="tw-border-border tw-p-0 tw-gap-0">
        <CardHeader className={headerClassName} onClick={handleClick}>
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Map className="tw-h-4 tw-w-4 tw-text-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">Land Use</span>
            </div>
            {accordionEnabled ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent
          className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
        >
          <Form {...form}>
            <div className="tw-rounded-lg tw-border tw-border-border tw-bg-muted/20 tw-p-4 tw-space-y-3">
              <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3">
                <div className="tw-space-y-1 tw-flex-1">
                  <Label className="tw-text-sm tw-font-medium tw-text-foreground">
                    Land use change in the last 20 years?
                  </Label>
                  <p className="tw-text-xs tw-text-muted-foreground tw-leading-relaxed">
                    Has any part of the field management practice changed between land use within
                    the last 20 years?
                  </p>
                </div>
                <ToggleGroup
                  type="single"
                  value={landUsageChange}
                  onValueChange={value => {
                    if (value) {
                      setLandUsageChange(value);
                      if (value === "no") {
                        resetLandUsageForm();
                      }
                    }
                  }}
                  disabled={lockStatus || disabled}
                  className="tw-inline-flex tw-shrink-0 tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-0.5 tw-gap-0"
                >
                  <ToggleGroupItem
                    value="no"
                    className="tw-min-w-[64px] tw-rounded-md tw-px-4 data-[state=on]:tw-bg-primary data-[state=on]:tw-text-primary-foreground data-[state=on]:tw-shadow-sm"
                  >
                    No
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="yes"
                    className="tw-min-w-[64px] tw-rounded-md tw-px-4 data-[state=on]:tw-bg-primary data-[state=on]:tw-text-primary-foreground data-[state=on]:tw-shadow-sm"
                  >
                    Yes
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            {isLandUseEnabled ? (
              <div className="tw-space-y-3">
                <p className="tw-text-xs tw-font-medium tw-text-muted-foreground tw-uppercase tw-tracking-wide">
                  Add land use change details
                </p>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-[1fr_1fr_1fr_1fr_auto] tw-items-start tw-gap-4">
                  <FormSelect
                    control={control}
                    name="landUseYearOfChange"
                    label="Year of change"
                    placeholder="Select Year"
                    options={yearOptions}
                    resetKey={formEntryKey}
                    required
                    errorMessage="Year of change is required"
                    disabled={disabled}
                  />
                  <FormInputNumber
                    control={control}
                    label="Allocation (%)"
                    placeholder="Allocation (%)"
                    name="landUsageAllocation"
                    enableMinus={false}
                    errorMessage="Allocation is required"
                    required
                    rules={{
                      validate: {
                        valueCheck: (value: any) => {
                          if (value === undefined || value === null || value === "") {
                            return "Allocation is required";
                          }
                          return validatePercentageNumber(value);
                        }
                      }
                    }}
                    disabled={disabled}
                  />
                  <FormSelect
                    control={control}
                    name="landUsageFrom"
                    label="Land use change - From"
                    placeholder="Select Land use change"
                    options={landUsageOptions}
                    resetKey={formEntryKey}
                    required
                    errorMessage="Land use change from is required"
                    disabled={disabled}
                  />
                  <FormSelect
                    control={control}
                    name="landUsageTo"
                    label="Land use change - To"
                    placeholder="Select Land use change"
                    options={landUsageOptions}
                    resetKey={formEntryKey}
                    required
                    errorMessage="Land use change to is required"
                    disabled={disabled}
                  />
                  <div className="tw-space-y-2 tw-mt-2">
                    <div
                      className="tw-text-sm tw-font-medium tw-leading-none tw-text-foreground tw-opacity-0 tw-select-none"
                      aria-hidden
                    >
                      Add
                    </div>
                    <TooltipProvider>
                      {isEditingEntry ? (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="lg"
                                className="!tw-h-10 !tw-w-10 !tw-px-0"
                                disabled={isAddDisabled}
                                onClick={addLandUsage}
                                aria-label="Update"
                              >
                                <Check className="tw-h-4 tw-w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="tw-text-xs">Update</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="lg"
                                variant="outline"
                                className="!tw-h-10 !tw-w-10 !tw-px-0"
                                disabled={isAddDisabled}
                                onClick={resetLandUsageForm}
                                aria-label="Cancel"
                              >
                                <X className="tw-h-4 tw-w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="tw-text-xs">Cancel</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      ) : hasErrors ? (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="lg"
                                className="!tw-h-10 !tw-w-10 !tw-px-0"
                                disabled={isAddDisabled}
                                onClick={addLandUsage}
                                aria-label="Add"
                              >
                                <Plus className="tw-h-4 tw-w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="tw-text-xs">Add</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                size="lg"
                                variant="outline"
                                className="!tw-h-10 !tw-w-10 !tw-px-0"
                                disabled={isAddDisabled}
                                onClick={resetLandUsageForm}
                                aria-label="Clear"
                              >
                                <X className="tw-h-4 tw-w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="tw-text-xs">Clear</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="lg"
                              className="!tw-h-10 !tw-w-10 !tw-px-0"
                              disabled={isAddDisabled}
                              onClick={addLandUsage}
                            >
                              <Plus className="tw-h-4 tw-w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw-text-xs">Add</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            ) : (
              <p className="tw-text-xs tw-text-muted-foreground tw-italic">
                No land use changes to record. Switch to Yes if field management practices changed
                in the last 20 years.
              </p>
            )}

            {currentLandUsageDetails?.length > 0 && (
              <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="tw-border-b tw-border-border hover:tw-bg-transparent">
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Year of Change
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Allocation (%)
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Land usage from
                      </TableHead>
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Land usage to
                      </TableHead>
                      {!lockStatus && (
                        <TableHead
                          className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground tw-text-right tw-w-24"
                          style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                        >
                          Actions
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentLandUsageDetails.map((item: any, index: number) => (
                      <TableRow
                        key={`landUsage_${item?.id ?? index}`}
                        className="hover:tw-bg-muted/20"
                      >
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                          {item.landUseYearOfChange}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                          {item.landUsageAllocation}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                          {getLandUsageLabel(item.landUsageFrom)}
                        </TableCell>
                        <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                          {getLandUsageLabel(item.landUsageTo)}
                        </TableCell>
                        {!lockStatus && (
                          <TableCell className="tw-px-4 tw-py-3">
                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="tw-h-7 tw-w-7 tw-p-0"
                                onClick={() => onEditHandler(item, index)}
                              >
                                <Pencil className="tw-h-3.5 tw-w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                                onClick={() => deleteLandUseData(item, index)}
                              >
                                <Trash2 className="tw-h-3.5 tw-w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Form>
          <CultivarSaveReminder section="landUse" hidden={lockStatus || disabled} />
        </CardContent>
        <ConfirmModal
          viewModal={visibleDeleteModal}
          closeModal={() => setVisibleDeleteModal(false)}
          cancel={() => {
            setVisibleDeleteModal(false);
            setDeleteValue({});
          }}
          approve={deleteLandUse}
          title={"Delete Land Usage"}
          subTitle={DELETE_CONFIRM_MESSAGES.landUse}
        />
      </Card>
    ) : null;
  }
);

export default LandUse;
